import React, { useState, Component } from 'react';
import { CheckCircleIcon, ShieldCheckIcon, ClockIcon, DollarSignIcon, BookOpenIcon, UsersIcon, AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, StarIcon, HeartIcon, SearchIcon, FileTextIcon, CalculatorIcon, AwardIcon, LockIcon, GiftIcon, ArrowRightIcon } from 'lucide-react';
// Types
interface FAQItem {
  question: string;
  answer: string;
}
interface Testimonial {
  name: string;
  age: number;
  location: string;
  quote: string;
  result: string;
}
// FAQ Accordion Component
function FAQAccordion({
  item,
  isOpen,
  onToggle
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-[#e8e4dc] last:border-b-0" data-id="element-0">
      <button onClick={onToggle} className="w-full py-5 px-6 flex items-center justify-between text-left hover:bg-[#f5f0e8] transition-colors rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6] focus:ring-offset-2" aria-expanded={isOpen} data-id="element-1">
        <span className="font-semibold text-[#1e3a5f] text-lg pr-4" data-id="element-2">
          {item.question}
        </span>
        {isOpen ? <ChevronUpIcon className="w-5 h-5 text-[#3b82f6] flex-shrink-0" data-id="element-3" /> : <ChevronDownIcon className="w-5 h-5 text-[#3b82f6] flex-shrink-0" data-id="element-4" />}
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`} data-id="element-5">
        <p className="px-6 text-gray-600 text-lg leading-relaxed" data-id="element-6">
          {item.answer}
        </p>
      </div>
    </div>;
}
// CTA Button Component
function CTAButton({
  children,
  className = '',
  pulse = false
}: {
  children: React.ReactNode;
  className?: string;
  pulse?: boolean;
}) {
  return <a href="https://vitalias.it.com/cart/44838886735926:1" target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2 bg-[#d4a853] hover:bg-[#c49943] text-white font-semibold text-lg md:text-xl px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#d4a853] focus:ring-offset-2 ${pulse ? 'btn-pulse' : ''} ${className}`} data-id="element-7">
      {children}
      <ArrowRightIcon className="w-5 h-5" data-id="element-8" />
    </a>;
}
// Testimonial Card Component
function TestimonialCard({
  testimonial
}: {
  testimonial: Testimonial;
}) {
  return <article className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow" data-id="element-9">
      <div className="flex items-center gap-1 mb-4" data-id="element-10">
        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-[#d4a853] text-[#d4a853]" data-id="element-11" />)}
      </div>
      <blockquote className="text-gray-700 text-lg mb-6 italic leading-relaxed" data-id="element-12">
        "{testimonial.quote}"
      </blockquote>
      <div className="flex items-center gap-4" data-id="element-13">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#3b82f6] to-[#1e3a5f] flex items-center justify-center text-white font-semibold text-xl" data-id="element-14">
          {testimonial.name.charAt(0)}
        </div>
        <div data-id="element-15">
          <p className="font-semibold text-[#1e3a5f]" data-id="element-16">
            {testimonial.name}, {testimonial.age} años
          </p>
          <p className="text-gray-500" data-id="element-17">{testimonial.location}</p>
          <p className="text-[#d4a853] font-medium text-sm mt-1" data-id="element-18">
            {testimonial.result}
          </p>
        </div>
      </div>
    </article>;
}
// Main App Component
export function App() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const testimonials: Testimonial[] = [{
    name: 'María Elena',
    age: 72,
    location: 'California',
    quote: 'Nunca imaginé que me faltaban 3 años de trabajo en mi historial. Gracias a esta guía, ahora recibo $312 más cada mes. ¡Es dinero que me pertenecía!',
    result: '+$312/mes recuperados'
  }, {
    name: 'Carmen Rosa',
    age: 68,
    location: 'Florida',
    quote: 'Como viuda, no sabía que tenía derecho a beneficios adicionales. El sistema Triple-Check me ayudó a entender todo sin necesidad de abogados.',
    result: '+$428/mes en beneficios de viudez'
  }, {
    name: 'Guadalupe',
    age: 70,
    location: 'Texas',
    quote: 'Estaba resignada a mi cheque pequeño. Esta guía me dio la claridad y el valor para reclamar lo que me corresponde. Ahora tengo paz mental.',
    result: '+$195/mes corregidos'
  }];
  const faqItems: FAQItem[] = [{
    question: '¿Necesito un abogado para hacer esto?',
    answer: 'No, absolutamente no. El sistema Triple-Check está diseñado para que cualquier persona pueda seguirlo paso a paso, sin tecnicismos legales. Todo está explicado en lenguaje sencillo, con ejemplos claros y formularios pre-llenados que puedes usar directamente.'
  }, {
    question: '¿Es complicado de entender?',
    answer: 'Para nada. La guía está escrita pensando en ti, con letra grande, instrucciones claras y ejemplos reales. Cada paso te dice exactamente qué hacer, dónde ir y qué decir. Es como tener una amiga experta guiándote de la mano.'
  }, {
    question: '¿Funciona para viudas o divorciadas?',
    answer: '¡Sí! De hecho, el Paso 2 del sistema Triple-Check está dedicado específicamente a los beneficios derivados: beneficios de viudez, beneficios por ex-cónyuge, y combinaciones que muchas mujeres desconocen. Estos son algunos de los beneficios más olvidados.'
  }, {
    question: '¿Cuánto tiempo toma ver resultados?',
    answer: 'Puedes completar la auditoría inicial en un fin de semana. Si encuentras errores o beneficios no reclamados, el proceso de corrección con el SSA puede tomar de 30 a 90 días, pero la guía te muestra exactamente cómo acelerar el proceso.'
  }, {
    question: '¿Qué pasa si no encuentro ningún error?',
    answer: 'Tendrás la tranquilidad de saber que estás recibiendo todo lo que te corresponde. Además, aprenderás estrategias para maximizar tus beneficios futuros. Y recuerda: tienes 30 días de garantía completa.'
  }, {
    question: '¿Esto es legal?',
    answer: '100% legal. Solo estás verificando que el Seguro Social tenga tu información correcta y reclamando beneficios que ya te pertenecen por ley. No es ningún truco ni vacío legal, es simplemente asegurarte de recibir lo que trabajaste para ganar.'
  }];
  return <main className="min-h-screen w-full bg-[#faf7f2]" data-id="element-19">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#1e3a5f] to-[#2d4a6f] text-white" data-id="element-20">
        <div className="absolute inset-0 opacity-10" data-id="element-21">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#d4a853] rounded-full blur-3xl" data-id="element-22"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#3b82f6] rounded-full blur-3xl" data-id="element-23"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24" data-id="element-24">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-25">
            <div className="text-center md:text-left" data-id="element-26">
              <p className="text-[#d4a853] font-medium text-lg mb-4 tracking-wide" data-id="element-27">
                PARA MUJERES DE 65+ AÑOS
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-28">
                ¿Te Deben Dinero?{' '}
                <span className="text-[#d4a853]" data-id="element-29">Descubre el Secreto</span> del
                Seguro Social
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed" data-id="element-30">
                Miles de mujeres jubiladas están dejando dinero sin reclamar.
                Descubre si tú eres una de ellas.
              </p>
              <CTAButton pulse data-id="element-31">Quiero Mi Guía Ahora</CTAButton>
              <p className="mt-6 text-blue-200 text-sm flex items-center justify-center md:justify-start gap-2" data-id="element-32">
                <ShieldCheckIcon className="w-4 h-4" data-id="element-33" />
                Garantía de 30 días • Acceso inmediato
              </p>
            </div>

            <div className="flex justify-center" data-id="element-34">
              <div className="relative" data-id="element-35">
                <div className="absolute -inset-4 bg-[#d4a853] rounded-2xl opacity-20 blur-xl" data-id="element-36"></div>
                <img src="https://cdn.magicpatterns.com/uploads/cNkZYeAe9c8TENRoC5wa6x/portada_del_libro_social_seguriti_3.png" alt="The Social Security Maximizer - Guía para maximizar tus beneficios del Seguro Social" className="relative rounded-xl shadow-2xl max-w-sm md:max-w-md w-full" data-id="element-37" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Lead Section */}
      <section className="py-16 md:py-24 bg-white" data-id="element-38">
        <div className="max-w-4xl mx-auto px-4" data-id="element-39">
          <div className="bg-[#faf7f2] rounded-2xl p-8 md:p-12 border-l-4 border-[#d4a853]" data-id="element-40">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-6" data-id="element-41">
              <span className="font-['Playfair_Display'] text-3xl text-[#1e3a5f] font-semibold" data-id="element-42">
                Imagínate
              </span>{' '}
              descubrir, a tus 68 años, que el Seguro Social{' '}
              <strong className="text-[#1e3a5f]" data-id="element-43">
                nunca incluyó dos años clave de tu trabajo
              </strong>
              .
            </p>
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-6" data-id="element-44">
              <strong className="text-[#1e3a5f]" data-id="element-45">Ana</strong>, una mujer
              jubilada de Texas, estaba viviendo con un cheque mensual ajustado,
              hasta que una simple auditoría de 3 pasos le permitió reclamar{' '}
              <span className="text-[#d4a853] font-bold" data-id="element-46">
                $275 extra cada mes
              </span>
              .
            </p>
            <p className="text-2xl md:text-3xl font-['Playfair_Display'] text-[#1e3a5f] font-semibold text-center mt-8" data-id="element-47">
              ¿Cuánto dinero podría estar esperándote?
            </p>
          </div>
        </div>
      </section>

      {/* Problem Agitation Section */}
      <section className="py-16 md:py-24 bg-[#faf7f2]" data-id="element-48">
        <div className="max-w-5xl mx-auto px-4" data-id="element-49">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] text-center mb-6" data-id="element-50">
            El Sistema Está Diseñado Para Confundirte
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto" data-id="element-51">
            No es tu culpa. El Seguro Social es uno de los sistemas más
            complicados que existen, y nadie te enseñó cómo navegarlo.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-52">
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-53">
              <div className="flex items-center gap-3 mb-4" data-id="element-54">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-55">
                  <AlertCircleIcon className="w-6 h-6 text-red-500" data-id="element-56" />
                </div>
                <h3 className="font-semibold text-xl text-[#1e3a5f]" data-id="element-57">
                  Lo Que Sientes
                </h3>
              </div>
              <ul className="space-y-4 text-lg text-gray-700" data-id="element-58">
                <li className="flex items-start gap-3" data-id="element-59">
                  <span className="text-red-400 mt-1" data-id="element-60">•</span>
                  <span data-id="element-61">
                    Miedo de que el sistema te esté reteniendo dinero por
                    errores
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-62">
                  <span className="text-red-400 mt-1" data-id="element-63">•</span>
                  <span data-id="element-64">
                    Confusión cada vez que intentas entender tus beneficios
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-65">
                  <span className="text-red-400 mt-1" data-id="element-66">•</span>
                  <span data-id="element-67">Preocupación por tu bienestar financiero futuro</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-68">
                  <span className="text-red-400 mt-1" data-id="element-69">•</span>
                  <span data-id="element-70">Temor de tener que depender de tus hijos</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-71">
              <div className="flex items-center gap-3 mb-4" data-id="element-72">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-73">
                  <ClockIcon className="w-6 h-6 text-gray-500" data-id="element-74" />
                </div>
                <h3 className="font-semibold text-xl text-[#1e3a5f]" data-id="element-75">
                  Lo Que Has Intentado
                </h3>
              </div>
              <ul className="space-y-4 text-lg text-gray-700" data-id="element-76">
                <li className="flex items-start gap-3" data-id="element-77">
                  <span className="text-gray-400 mt-1" data-id="element-78">✗</span>
                  <span data-id="element-79">
                    Las guías oficiales son complicadas y llenas de jerga legal
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-80">
                  <span className="text-gray-400 mt-1" data-id="element-81">✗</span>
                  <span data-id="element-82">Los abogados son costosos y no siempre necesarios</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-83">
                  <span className="text-gray-400 mt-1" data-id="element-84">✗</span>
                  <span data-id="element-85">
                    Llamar al SSA significa horas de espera sin respuestas
                    claras
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-86">
                  <span className="text-gray-400 mt-1" data-id="element-87">✗</span>
                  <span data-id="element-88">No sabes por dónde empezar ni qué preguntar</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-[#1e3a5f] rounded-2xl p-8 md:p-10 text-white text-center" data-id="element-89">
            <HeartIcon className="w-12 h-12 mx-auto mb-4 text-[#d4a853]" data-id="element-90" />
            <p className="text-xl md:text-2xl leading-relaxed" data-id="element-91">
              Trabajaste toda tu vida. Tienes{' '}
              <strong data-id="element-92">orgullo de tu historia laboral</strong>. Mereces recibir
              cada centavo que te corresponde, con{' '}
              <strong data-id="element-93">claridad y control</strong> sobre tu futuro financiero.
            </p>
          </div>
        </div>
      </section>

      {/* Mechanism Reveal Section */}
      <section className="py-16 md:py-24 bg-white" data-id="element-94">
        <div className="max-w-5xl mx-auto px-4" data-id="element-95">
          <div className="text-center mb-12" data-id="element-96">
            <p className="text-[#d4a853] font-semibold text-lg mb-3" data-id="element-97">
              LA SOLUCIÓN
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-[#1e3a5f] mb-6" data-id="element-98">
              El Sistema Triple-Check Audit
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-99">
              Un método paso a paso para detectar brechas en tu historial,
              beneficios olvidados y errores de cálculo —{' '}
              <strong data-id="element-100">sin tecnicismos legales</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-101">
            <div className="relative" data-id="element-102">
              <div className="bg-[#faf7f2] rounded-2xl p-8 h-full border-2 border-transparent hover:border-[#d4a853] transition-colors" data-id="element-103">
                <div className="w-16 h-16 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-2xl font-bold mb-6" data-id="element-104">
                  1
                </div>
                <div className="flex items-center gap-3 mb-4" data-id="element-105">
                  <SearchIcon className="w-6 h-6 text-[#3b82f6]" data-id="element-106" />
                  <h3 className="font-semibold text-xl text-[#1e3a5f]" data-id="element-107">
                    Paso 1
                  </h3>
                </div>
                <h4 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1e3a5f] mb-4" data-id="element-108">
                  Revisión del Historial Laboral
                </h4>
                <p className="text-gray-600 text-lg" data-id="element-109">
                  Aprende a obtener y revisar tu historial completo para
                  detectar años de trabajo faltantes o mal reportados.
                </p>
              </div>
            </div>

            <div className="relative" data-id="element-110">
              <div className="bg-[#faf7f2] rounded-2xl p-8 h-full border-2 border-transparent hover:border-[#d4a853] transition-colors" data-id="element-111">
                <div className="w-16 h-16 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-2xl font-bold mb-6" data-id="element-112">
                  2
                </div>
                <div className="flex items-center gap-3 mb-4" data-id="element-113">
                  <UsersIcon className="w-6 h-6 text-[#3b82f6]" data-id="element-114" />
                  <h3 className="font-semibold text-xl text-[#1e3a5f]" data-id="element-115">
                    Paso 2
                  </h3>
                </div>
                <h4 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1e3a5f] mb-4" data-id="element-116">
                  Análisis de Beneficios Derivados
                </h4>
                <p className="text-gray-600 text-lg" data-id="element-117">
                  Descubre beneficios por viudez, divorcio o matrimonio que
                  podrías estar dejando sobre la mesa.
                </p>
              </div>
            </div>

            <div className="relative" data-id="element-118">
              <div className="bg-[#faf7f2] rounded-2xl p-8 h-full border-2 border-transparent hover:border-[#d4a853] transition-colors" data-id="element-119">
                <div className="w-16 h-16 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-2xl font-bold mb-6" data-id="element-120">
                  3
                </div>
                <div className="flex items-center gap-3 mb-4" data-id="element-121">
                  <CalculatorIcon className="w-6 h-6 text-[#3b82f6]" data-id="element-122" />
                  <h3 className="font-semibold text-xl text-[#1e3a5f]" data-id="element-123">
                    Paso 3
                  </h3>
                </div>
                <h4 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1e3a5f] mb-4" data-id="element-124">
                  Detección de Errores de Cálculo
                </h4>
                <p className="text-gray-600 text-lg" data-id="element-125">
                  Verifica que el SSA haya calculado correctamente tu beneficio
                  usando las fórmulas correctas.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12" data-id="element-126">
            <CTAButton data-id="element-127">Quiero Empezar Mi Auditoría</CTAButton>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#1e3a5f] to-[#2d4a6f] text-white" data-id="element-128">
        <div className="max-w-5xl mx-auto px-4" data-id="element-129">
          <div className="text-center mb-12" data-id="element-130">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold mb-6" data-id="element-131">
              Lo Que Descubrirás en Esta Guía
            </h2>
            <p className="text-xl text-blue-100" data-id="element-132">
              Todo lo que necesitas para tomar control de tus beneficios
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-133">
            <div className="flex items-start gap-4 bg-white/10 rounded-xl p-6 backdrop-blur-sm" data-id="element-134">
              <CheckCircleIcon className="w-7 h-7 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-135" />
              <p className="text-lg" data-id="element-136">
                Cómo obtener tu historial laboral completo del SSA en minutos
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white/10 rounded-xl p-6 backdrop-blur-sm" data-id="element-137">
              <CheckCircleIcon className="w-7 h-7 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-138" />
              <p className="text-lg" data-id="element-139">
                Los 5 errores más comunes que reducen tu cheque mensual
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white/10 rounded-xl p-6 backdrop-blur-sm" data-id="element-140">
              <CheckCircleIcon className="w-7 h-7 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-141" />
              <p className="text-lg" data-id="element-142">
                Beneficios de viudez y divorcio que el SSA no te menciona
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white/10 rounded-xl p-6 backdrop-blur-sm" data-id="element-143">
              <CheckCircleIcon className="w-7 h-7 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-144" />
              <p className="text-lg" data-id="element-145">
                Cartas modelo para reclamar correcciones sin abogado
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white/10 rounded-xl p-6 backdrop-blur-sm" data-id="element-146">
              <CheckCircleIcon className="w-7 h-7 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-147" />
              <p className="text-lg" data-id="element-148">
                Cómo verificar si tu beneficio fue calculado correctamente
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white/10 rounded-xl p-6 backdrop-blur-sm" data-id="element-149">
              <CheckCircleIcon className="w-7 h-7 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-150" />
              <p className="text-lg" data-id="element-151">
                Estrategias para maximizar beneficios si aún no te jubilaste
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white/10 rounded-xl p-6 backdrop-blur-sm" data-id="element-152">
              <CheckCircleIcon className="w-7 h-7 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-153" />
              <p className="text-lg" data-id="element-154">
                Checklist de auditoría para no olvidar ningún paso
              </p>
            </div>
            <div className="flex items-start gap-4 bg-white/10 rounded-xl p-6 backdrop-blur-sm" data-id="element-155">
              <CheckCircleIcon className="w-7 h-7 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-156" />
              <p className="text-lg" data-id="element-157">
                Números y contactos directos para acelerar tus trámites
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 bg-[#faf7f2]" data-id="element-158">
        <div className="max-w-6xl mx-auto px-4" data-id="element-159">
          <div className="text-center mb-12" data-id="element-160">
            <p className="text-[#d4a853] font-semibold text-lg mb-3" data-id="element-161">
              HISTORIAS REALES
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-6" data-id="element-162">
              Mujeres Como Tú Ya Están Recuperando Lo Suyo
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-163">
            {testimonials.map((testimonial, index) => <TestimonialCard key={index} testimonial={testimonial} data-id="element-164" />)}
          </div>
        </div>
      </section>

      {/* What's Inside Section */}
      <section className="py-16 md:py-24 bg-white" data-id="element-165">
        <div className="max-w-5xl mx-auto px-4" data-id="element-166">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-167">
            <div data-id="element-168">
              <p className="text-[#d4a853] font-semibold text-lg mb-3" data-id="element-169">
                CONTENIDO DE LA GUÍA
              </p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-8" data-id="element-170">
                Todo Lo Que Incluye Tu Guía
              </h2>

              <div className="space-y-5" data-id="element-171">
                <div className="flex items-start gap-4" data-id="element-172">
                  <div className="w-10 h-10 rounded-lg bg-[#faf7f2] flex items-center justify-center flex-shrink-0" data-id="element-173">
                    <BookOpenIcon className="w-5 h-5 text-[#3b82f6]" data-id="element-174" />
                  </div>
                  <div data-id="element-175">
                    <h4 className="font-semibold text-[#1e3a5f] text-lg" data-id="element-176">
                      Capítulo 1: Tu Historial Laboral
                    </h4>
                    <p className="text-gray-600" data-id="element-177">
                      Cómo obtenerlo, leerlo y detectar errores
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-178">
                  <div className="w-10 h-10 rounded-lg bg-[#faf7f2] flex items-center justify-center flex-shrink-0" data-id="element-179">
                    <UsersIcon className="w-5 h-5 text-[#3b82f6]" data-id="element-180" />
                  </div>
                  <div data-id="element-181">
                    <h4 className="font-semibold text-[#1e3a5f] text-lg" data-id="element-182">
                      Capítulo 2: Beneficios Derivados
                    </h4>
                    <p className="text-gray-600" data-id="element-183">
                      Viudez, divorcio y beneficios conyugales
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-184">
                  <div className="w-10 h-10 rounded-lg bg-[#faf7f2] flex items-center justify-center flex-shrink-0" data-id="element-185">
                    <CalculatorIcon className="w-5 h-5 text-[#3b82f6]" data-id="element-186" />
                  </div>
                  <div data-id="element-187">
                    <h4 className="font-semibold text-[#1e3a5f] text-lg" data-id="element-188">
                      Capítulo 3: Errores de Cálculo
                    </h4>
                    <p className="text-gray-600" data-id="element-189">
                      Fórmulas simplificadas y verificación
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-190">
                  <div className="w-10 h-10 rounded-lg bg-[#faf7f2] flex items-center justify-center flex-shrink-0" data-id="element-191">
                    <FileTextIcon className="w-5 h-5 text-[#3b82f6]" data-id="element-192" />
                  </div>
                  <div data-id="element-193">
                    <h4 className="font-semibold text-[#1e3a5f] text-lg" data-id="element-194">
                      Capítulo 4: Cartas y Formularios
                    </h4>
                    <p className="text-gray-600" data-id="element-195">Plantillas listas para usar</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-196">
                  <div className="w-10 h-10 rounded-lg bg-[#faf7f2] flex items-center justify-center flex-shrink-0" data-id="element-197">
                    <AwardIcon className="w-5 h-5 text-[#3b82f6]" data-id="element-198" />
                  </div>
                  <div data-id="element-199">
                    <h4 className="font-semibold text-[#1e3a5f] text-lg" data-id="element-200">
                      BONUS: Checklist de Auditoría
                    </h4>
                    <p className="text-gray-600" data-id="element-201">Guía rápida paso a paso</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center" data-id="element-202">
              <div className="relative" data-id="element-203">
                <div className="absolute -inset-4 bg-[#3b82f6] rounded-2xl opacity-10 blur-xl" data-id="element-204"></div>
                <img src="https://cdn.magicpatterns.com/uploads/cNkZYeAe9c8TENRoC5wa6x/portada_del_libro_social_seguriti_3.png" alt="The Social Security Maximizer - Vista previa del contenido" className="relative rounded-xl shadow-xl max-w-xs md:max-w-sm w-full" data-id="element-205" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-16 md:py-20 bg-[#faf7f2]" data-id="element-206">
        <div className="max-w-4xl mx-auto px-4" data-id="element-207">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-2 border-[#d4a853]" data-id="element-208">
            <div className="text-center" data-id="element-209">
              <div className="w-20 h-20 rounded-full bg-[#d4a853] mx-auto mb-6 flex items-center justify-center" data-id="element-210">
                <ShieldCheckIcon className="w-10 h-10 text-white" data-id="element-211" />
              </div>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-6" data-id="element-212">
                Garantía de 30 Días Sin Riesgo
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto" data-id="element-213">
                Si por cualquier razón no estás satisfecha con la guía,
                simplemente escríbenos dentro de los primeros 30 días y te
                devolvemos el 100% de tu dinero.{' '}
                <strong data-id="element-214">Sin preguntas, sin complicaciones.</strong>
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-gray-600" data-id="element-215">
                <div className="flex items-center gap-2" data-id="element-216">
                  <LockIcon className="w-5 h-5 text-[#3b82f6]" data-id="element-217" />
                  <span data-id="element-218">Pago 100% seguro</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-219">
                  <ClockIcon className="w-5 h-5 text-[#3b82f6]" data-id="element-220" />
                  <span data-id="element-221">Acceso inmediato</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-222">
                  <GiftIcon className="w-5 h-5 text-[#3b82f6]" data-id="element-223" />
                  <span data-id="element-224">Bonus incluidos</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="comprar" className="py-16 md:py-24 bg-gradient-to-b from-[#1e3a5f] to-[#2d4a6f] text-white" data-id="element-225">
        <div className="max-w-4xl mx-auto px-4" data-id="element-226">
          <div className="text-center mb-12" data-id="element-227">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold mb-6" data-id="element-228">
              Recupera Lo Que Te Pertenece
            </h2>
            <p className="text-xl text-blue-100" data-id="element-229">
              Una pequeña inversión que puede significar miles de dólares en
              beneficios
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 text-gray-900 shadow-2xl" data-id="element-230">
            <div className="text-center mb-8" data-id="element-231">
              <p className="text-gray-500 text-lg mb-2" data-id="element-232">
                Precio especial de lanzamiento
              </p>
              <div className="flex items-center justify-center gap-3 mb-4" data-id="element-233">
                <span className="text-3xl text-gray-400 line-through" data-id="element-234">$47</span>
                <span className="font-['Playfair_Display'] text-6xl md:text-7xl font-bold text-[#1e3a5f]" data-id="element-235">
                  $10
                </span>
              </div>
              <p className="text-[#d4a853] font-semibold" data-id="element-236">
                Ahorra $37 — Oferta por tiempo limitado
              </p>
            </div>

            <div className="border-t border-b border-gray-200 py-8 mb-8" data-id="element-237">
              <h3 className="font-semibold text-xl text-[#1e3a5f] mb-6 text-center" data-id="element-238">
                Lo que recibes:
              </h3>
              <div className="space-y-4 max-w-md mx-auto" data-id="element-239">
                <div className="flex items-center gap-3" data-id="element-240">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-241" />
                  <span className="text-lg" data-id="element-242">
                    Guía completa "Social Security Maximizer"
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-243">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-244" />
                  <span className="text-lg" data-id="element-245">
                    Sistema Triple-Check Audit paso a paso
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-246">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-247" />
                  <span className="text-lg" data-id="element-248">
                    Cartas y formularios listos para usar
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-249">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-250" />
                  <span className="text-lg" data-id="element-251">
                    Checklist de auditoría imprimible
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-252">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-253" />
                  <span className="text-lg" data-id="element-254">
                    Acceso de por vida + actualizaciones
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-255">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-256" />
                  <span className="text-lg" data-id="element-257">Garantía de 30 días</span>
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-258">
              <CTAButton className="text-xl px-12 py-5" pulse data-id="element-259">
                ¡Sí! Quiero Mi Guía Ahora
              </CTAButton>
              <p className="mt-4 text-gray-500 text-sm flex items-center justify-center gap-2" data-id="element-260">
                <LockIcon className="w-4 h-4" data-id="element-261" />
                Pago seguro con encriptación SSL
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white" data-id="element-262">
        <div className="max-w-3xl mx-auto px-4" data-id="element-263">
          <div className="text-center mb-12" data-id="element-264">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4" data-id="element-265">
              Preguntas Frecuentes
            </h2>
            <p className="text-xl text-gray-600" data-id="element-266">
              Resolvemos tus dudas más comunes
            </p>
          </div>

          <div className="bg-[#faf7f2] rounded-2xl overflow-hidden" data-id="element-267">
            {faqItems.map((item, index) => <FAQAccordion key={index} item={item} isOpen={openFAQ === index} onToggle={() => setOpenFAQ(openFAQ === index ? null : index)} data-id="element-268" />)}
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-16 md:py-24 bg-[#faf7f2]" data-id="element-269">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-270">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-[#1e3a5f] mb-6" data-id="element-271">
            No Dejes Tu Dinero Sobre la Mesa
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto" data-id="element-272">
            Cada mes que pasa sin revisar tus beneficios es dinero que podrías
            estar perdiendo. Toma acción hoy y recupera lo que te pertenece.
          </p>
          <CTAButton className="text-xl" pulse data-id="element-273">
            Obtener Mi Guía Ahora — Solo $10
          </CTAButton>
          <p className="mt-6 text-gray-500" data-id="element-274">
            Acceso inmediato • Garantía de 30 días • Sin riesgo
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e3a5f] text-white py-8" data-id="element-275">
        <div className="max-w-5xl mx-auto px-4 text-center" data-id="element-276">
          <p className="text-blue-200 text-sm" data-id="element-277">
            © 2026 Social Security Maximizer. Todos los derechos reservados.
          </p>
          <p className="text-blue-300 text-xs mt-2" data-id="element-278">
            Esta guía es solo para fines educativos y no constituye asesoría
            legal o financiera.
          </p>
        </div>
      </footer>
    </main>;
}